/**
 * Pure lateral-movement player sim.
 *
 * Stage 1a — owns the player's lateral strafe (accel / damping / clamp),
 * the forward-speed ramp, distance accumulation, and kick cooldown tick.
 * Deterministic: given identical (PlayerState, InputState, dt) sequences
 * the result is identical. No three.js, no DOM, no allocation in hot path.
 *
 * Coordinate convention (see src/sim/types.ts):
 * - Player stays at z = 0; forward travel accumulates into `distance`.
 * - x is lateral, corridor centered on 0, clamped to ±strafeHalfWidth (inside
 *   the outer lanes' contact band — see the clamp comment in stepPlayer).
 */

import { TUNING } from "./tuning";
import type { PlayerState, InputState } from "./types";

const P = TUNING.player;
const R = TUNING.road;

/** Forward-speed ramp slope (m/s per second of ramp progress). */
const SPEED_RAMP_SLOPE = (P.speedMax - P.speedStart) / P.speedRampDuration;
/** Lateral damping factor applied per second: v *= exp(-k*dt). */
const DAMP_FACTOR = P.lateralDamping;

/**
 * Create a fresh player state at the start of a run.
 * Lateral position/velocity zeroed; forward speed at the start value.
 */
export function createPlayerState(): PlayerState {
  return {
    x: 0,
    vx: 0,
    forwardSpeed: P.speedStart,
    distance: 0,
    alive: true,
    kickCooldownRemaining: 0,
    lastKickLeg: null,
  };
}

/**
 * Advance the player one fixed sim step.
 *
 * Mutates `p` in place (zero allocation). The kick *edge* itself (consuming
 * `input.kick`, choosing a leg, targeting) is handled by the kick system in
 * Stage 1c; here we only tick down the cooldown so the pip animates even
 * before the full kick system lands. `input.kick` is consumed by the caller's
 * kick step; this function treats it as inert.
 *
 * @param p     player state (mutated)
 * @param input sampled input for this step (left/right held, kick edge)
 * @param dt    fixed step seconds (TUNING.sim.dt)
 */
export function stepPlayer(p: PlayerState, input: InputState, dt: number): void {
  if (!p.alive) return;

  // --- Lateral movement -------------------------------------------------
  const left = input.left;
  const right = input.right;

  if (left !== right) {
    // Exactly one direction held: accelerate toward it.
    const dir = right ? 1 : -1;
    p.vx += dir * P.lateralAccel * dt;
    // Clamp speed magnitude to lateralMaxSpeed (preserve sign).
    if (p.vx > P.lateralMaxSpeed) p.vx = P.lateralMaxSpeed;
    else if (p.vx < -P.lateralMaxSpeed) p.vx = -P.lateralMaxSpeed;
  } else {
    // No input (or both held = cancel): exponential damping toward rest.
    // v_next = v * exp(-k*dt); closed-form, frame-rate independent.
    const k = Math.exp(-DAMP_FACTOR * dt);
    p.vx *= k;
    // Snap to rest when effectively still to avoid eternal float drift.
    if (p.vx > -1e-3 && p.vx < 1e-3) p.vx = 0;
  }

  // Integrate position.
  p.x += p.vx * dt;

  // Clamp to the strafe envelope; hitting either wall zeroes lateral velocity.
  // NOTE: `strafeHalfWidth` is DERIVED (tuning.ts), not tuned: the player may
  // ride every metre of asphalt between the guardrails, so the clamp is the
  // corridor half-width (4.3 m = 3 arcade lanes of 2.7 m + a 0.25 m shoulder
  // each side, where the guardrail box starts) minus the visible bike
  // half-width (0.45 m) => 3.85 m. The outer edge therefore just touches the
  // road edge at full lock.
  // A player parked at the clamp is out of reach of every lane-centred CAR
  // (outer lane centre 2.7 m + the widest car contact half-width, suv 1.125 m,
  // = 3.825 m) but IS inside the band of a lane-centred TRUCK (boxtruck 4.10 m,
  // semi 4.15 m). Shoulder camping is defeated by the spawner instead of by the
  // clamp: the guaranteed path never holds one lane for more than
  // flow.pathTransitionMax, the stream fills every lane the path is not in, and
  // flow.outerTruckInterval guarantees a TRUCK in each outer lane at least every
  // 12 s (spawner.ts ANTI-CAMP).
  const half = R.strafeHalfWidth;
  if (p.x > half) {
    p.x = half;
    p.vx = 0;
  } else if (p.x < -half) {
    p.x = -half;
    p.vx = 0;
  }

  // --- Forward speed ramp ----------------------------------------------
  // Linear ramp from speedStart to speedMax over speedRampDuration seconds.
  // Time progresses with distance/forwardSpeed; we use distance-derived time
  // so pausing (no steps) freezes the ramp too. Equivalent to integrating
  // slope*dt while capped at max.
  if (p.forwardSpeed < P.speedMax) {
    p.forwardSpeed += SPEED_RAMP_SLOPE * dt;
    if (p.forwardSpeed > P.speedMax) p.forwardSpeed = P.speedMax;
  }

  // --- Distance + cooldown ---------------------------------------------
  p.distance += p.forwardSpeed * dt;

  if (p.kickCooldownRemaining > 0) {
    p.kickCooldownRemaining -= dt;
    if (p.kickCooldownRemaining < 0) p.kickCooldownRemaining = 0;
  }
}
